import { CaretDown, CaretUp, Compare, ShapeExcept } from '@carbon/react/icons';
import { FormattedCount } from 'components/formatted-count';
import {
  createColumnHelper,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Date } from 'components/date';
import { Link } from 'components/link';
import { ColumnFilter } from 'components/table/column-filter';
import { TableActions } from 'components/table/table-actions';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { Fragment, useMemo, useRef, useState } from 'react';
import { format2 } from '../../../utils/number';
import { isNil, partition } from 'lodash-es';
import { emptyPinStyleGenerator } from '../table';
import styles from './slots-details.module.scss';
import { createResourceAtom } from 'lib/atoms';
import {
  UnitIntersectionAnalysisItem,
  UnitIntersectionAnalysisResponse,
} from '../types';
import {
  formatAssignmentType,
  formatUnitAgreementType,
  NULL_DISPLAY_VALUE,
} from '../utils';
import { useAtomValue } from 'jotai';
import { formatResource } from 'utils/misc';
import {
  Button,
  OverflowMenu,
  RadioButtonGroup,
  RadioButton,
  Search,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from '@carbon/react';
import {
  WELL_STATUS_COUNT_COLUMNS,
  wellStatusCounts,
  type WellStatusCounts,
} from 'utils/slot-categories';

// Columns whose displayed text differs from the stored value. Every other
// column renders its accessor value as-is.
const DISPLAY_FORMATTERS: Record<
  string,
  (item: UnitIntersectionAnalysisItem) => string
> = {
  unitAgreementType: (item) => formatUnitAgreementType(item.unitAgreementType),
  assignmentType: (item) => formatAssignmentType(item.assignmentType),
};

/**
 * Matches a term against the value rendered in the cell rather than the stored
 * one, so searching 'D&S' finds a `d_and_s` row.
 *
 * Shared by the per-column filters and the search box deliberately: TanStack
 * applies a column's own `filterFn` to `columnFilters` only, so the global
 * filter would otherwise fall back to `includesString` over raw accessor
 * values and 'D&S' would match nothing.
 *
 * @param {Row<UnitIntersectionAnalysisItem>} row - Row being tested.
 * @param {string} columnId - Column whose value the term is matched against.
 * @param {string} filterValue - The user's search or column-filter term.
 * @return {boolean} True when the rendered value contains the term.
 */
const matchesDisplayValue: FilterFn<UnitIntersectionAnalysisItem> = (
  row,
  columnId,
  filterValue
) => {
  if (!filterValue) return true;

  const displayed =
    DISPLAY_FORMATTERS[columnId]?.(row.original) ?? row.getValue(columnId);

  return String(displayed ?? '')
    .toLowerCase()
    .includes(String(filterValue).toLowerCase());
};

export interface SlotsDetailProps {
  unitName: string;
  selectedWellUWI: string | null;
  selectedWellName: string | null;
  /**
   * Unit of the selected row. Rows are per (well, agreement), so the unit is
   * part of a row's identity — without it a well allocated under two units
   * selects both of its rows at once.
   */
  selectedSlotUnit: string | null;
  onWellSelect: (
    wellUWI: string | null,
    wellName: string | null,
    slotUnit: string | null
  ) => void;
  /**
   * Opens the allocation manager comparison. The Compare control renders
   * disabled until a handler is supplied.
   */
  onCompare?: () => void;
}

const columnHelper =
  createColumnHelper<UnitIntersectionAnalysisResponse['items'][number]>();

// Component for slots table
const SlotsTable = ({
  unitName,
  selectedWellUWI,
  selectedWellName,
  selectedSlotUnit,
  onWellSelect,
  onCompare,
}: {
  unitName: string;
  selectedWellUWI: string | null;
  selectedWellName: string | null;
  /**
   * Unit of the selected row. Rows are per (well, agreement), so the unit is
   * part of a row's identity — without it a well allocated under two units
   * selects both of its rows at once.
   */
  selectedSlotUnit: string | null;
  onWellSelect: (
    wellUWI: string | null,
    wellName: string | null,
    slotUnit: string | null
  ) => void;
  onCompare?: () => void;
}) => {
  const [coverageThreshold, setCoverageThreshold] = useState<number>(0);
  // Client-side search across every visible column of the table below.
  const [globalFilter, setGlobalFilter] = useState('');
  const tableId = `slots-details-table`;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);
  const [columnVisibility, setColumnVisibility] = useColumnVisibility(tableId);

  const {
    columnFilters,
    setColumnFilters,
    isColumnFilterActive,
    setIsColumnFilterActive,
  } = useColumnFilter(tableId);

  const uiaSlotsAtom = useMemo(() => {
    const queryParams =
      coverageThreshold === 0 ? '' : `?coverage_threshold=${coverageThreshold}`;
    return createResourceAtom<UnitIntersectionAnalysisResponse>(
      `/development_areas/${unitName}/unit_intersection_analysis${queryParams}`
    );
  }, [coverageThreshold, unitName]);

  const uiaSlots = useAtomValue(uiaSlotsAtom);

  const { data: uiaSlotsData, loading: uiaSlotsLoading } =
    formatResource(uiaSlots);

  const columns = useMemo(() => {
    // Helper function to wrap content in bold when assignedToUnit is "Yes"
    const wrapBold = (
      content: string | JSX.Element,
      assignedToUnit: string | null
    ) => {
      if (assignedToUnit === 'Yes') {
        return <strong>{content}</strong>;
      }
      return content;
    };

    return [
      // 1. slotId
      columnHelper.accessor('slotId', {
        id: 'slotId',
        header: 'Slot ID',
        size: 150,
        cell: ({ row }) => {
          const slotId = row.original.slotId;
          // For slots (no slotId/UWI), show wellName in this column but not as a link
          const displayValue = slotId || row.original.wellName;
          let content;
          if (!displayValue) {
            content = NULL_DISPLAY_VALUE;
          } else if (slotId) {
            // Only make it a hyperlink if we have a real slotId (UWI)
            content = (
              <Link to={`/wells/${slotId}`} target="_blank">
                {displayValue}
              </Link>
            );
          } else {
            // No slotId, just show wellName as plain text
            content = displayValue;
          }
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 2. srpWellStatus
      columnHelper.accessor('srpWellStatus', {
        id: 'srpWellStatus',
        header: 'SRP Well Status',
        size: 150,
        cell: ({ row }) => {
          const content = row.original.srpWellStatus || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 3. wellName
      columnHelper.accessor('wellName', {
        id: 'wellName',
        header: 'Well Name',
        size: 120,
        cell: ({ row }) => {
          const content = row.original.wellName || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 4. unit
      columnHelper.accessor('unit', {
        id: 'unit',
        header: 'Unit',
        size: 150,
        cell: ({ row }) => {
          const unit = row.original.unit;
          const isAssigned = row.original.assignedToUnit === 'Yes';

          if (!unit) {
            return NULL_DISPLAY_VALUE;
          }

          // Add hyperlink for slots NOT assigned to the current unit and not the current page's unit
          const showLink = !isAssigned && unit !== unitName;
          const content = showLink ? (
            <Link
              to={`/development_areas/${unit}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {unit}
            </Link>
          ) : (
            unit
          );

          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 5. unitAgreementType
      columnHelper.accessor('unitAgreementType', {
        id: 'unitAgreementType',
        header: 'Type',
        size: 100,
        cell: ({ row }) => {
          const content = formatUnitAgreementType(
            row.original.unitAgreementType
          );
          return wrapBold(content, row.original.assignedToUnit);
        },
        filterFn: matchesDisplayValue,
      }),
      // 6. assignedToUnit
      columnHelper.accessor('assignedToUnit', {
        id: 'assignedToUnit',
        header: 'Assigned To Unit',
        size: 150,
        cell: ({ row }) => {
          const content = row.original.assignedToUnit || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 7. assignmentType
      columnHelper.accessor('assignmentType', {
        id: 'assignmentType',
        header: 'Assignment Type',
        size: 150,
        cell: ({ row }) => {
          const content = formatAssignmentType(row.original.assignmentType);
          return wrapBold(content, row.original.assignedToUnit);
        },
        filterFn: matchesDisplayValue,
      }),
      // 8. operator
      columnHelper.accessor('operator', {
        id: 'operator',
        header: 'Operator',
        size: 150,
        cell: ({ row }) => {
          const content = row.original.operator || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 9. holeDirection
      columnHelper.accessor('holeDirection', {
        id: 'holeDirection',
        header: 'Hole Direction',
        size: 120,
        cell: ({ row }) => {
          const content = row.original.holeDirection || NULL_DISPLAY_VALUE;
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 10. permitDate
      columnHelper.accessor('permitDate', {
        id: 'permitDate',
        header: 'Permit Date',
        size: 120,
        cell: ({ row }) => {
          const permitDate = row.original.permitDate;
          const content = permitDate ? (
            <Date date={permitDate} />
          ) : (
            NULL_DISPLAY_VALUE
          );
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 11. spudDate
      columnHelper.accessor('spudDate', {
        id: 'spudDate',
        header: 'Spud Date',
        size: 120,
        cell: ({ row }) => {
          const spudDate = row.original.spudDate;
          const content = spudDate ? (
            <Date date={spudDate} />
          ) : (
            NULL_DISPLAY_VALUE
          );
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 12. rigReleaseDate
      columnHelper.accessor('rigReleaseDate', {
        id: 'rigReleaseDate',
        header: 'Rig Release Date',
        size: 120,
        cell: ({ row }) => {
          const rigReleaseDate = row.original.rigReleaseDate;
          const content = rigReleaseDate ? (
            <Date date={rigReleaseDate} />
          ) : (
            NULL_DISPLAY_VALUE
          );
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 13. completionDate
      columnHelper.accessor('completionDate', {
        id: 'completionDate',
        header: 'Completion Date',
        size: 120,
        cell: ({ row }) => {
          const completionDate = row.original.completionDate;
          const content = completionDate ? (
            <Date date={completionDate} />
          ) : (
            NULL_DISPLAY_VALUE
          );
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 14. firstProductionDate
      columnHelper.accessor('firstProductionDate', {
        id: 'firstProductionDate',
        header: 'First Production Date',
        size: 150,
        cell: ({ row }) => {
          const firstProductionDate = row.original.firstProductionDate;
          const content = firstProductionDate ? (
            <Date date={firstProductionDate} />
          ) : (
            NULL_DISPLAY_VALUE
          );
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
      // 15. coverageRatio
      columnHelper.accessor('coverageRatio', {
        id: 'coverageRatio',
        header: 'Coverage Ratio',
        size: 120,
        cell: ({ row }) => {
          const content = format2(row.original.coverageRatio);
          return wrapBold(content, row.original.assignedToUnit);
        },
      }),
    ];
  }, [unitName]);

  const tableData = useMemo(() => {
    const items = uiaSlotsData?.items || [];
    // Sort so assigned wells (assignedToUnit === 'Yes') appear at the top
    return [...items].sort((a, b) => {
      if (a.assignedToUnit === 'Yes' && b.assignedToUnit !== 'Yes') return -1;
      if (a.assignedToUnit !== 'Yes' && b.assignedToUnit === 'Yes') return 1;
      return 0;
    });
  }, [uiaSlotsData?.items]);

  // Each Create New Well status is its own column (WellStatusCounts, from the shared list).
  interface SummaryRow extends WellStatusCounts {
    summary: string;
    producing: number;
    duc: number;
    permit: number;
    ltd: number;
    shutIn: number;
    inactive: number;
    expiredPermit: number;
    total: number;
  }

  const slotSummaryData = useMemo((): SummaryRow[] => {
    const statusMap: Record<
      string,
      keyof Omit<SummaryRow, 'summary' | 'total'>
    > = {
      producing: 'producing',
      duc: 'duc',
      permit: 'permit',
      shut_in: 'shutIn',
      expired_permit: 'expiredPermit',
      inactive: 'inactive',
      ltd: 'ltd',
      // Each Create New Well status its own column, so the row adds up to Total.
      ...Object.fromEntries(
        WELL_STATUS_COUNT_COLUMNS.map(({ key, status }) => [status, key])
      ),
    };

    // The table lists one row per agreement, so a well allocated under a D&S and two
    // JOAs appears three times. The summary counts WELLS, not agreements — without
    // this, such a well is counted once as Assigned and twice as Not Assigned.
    //
    // Keeps the first occurrence: the backend orders each well's agreements with this
    // unit's first, so that row is the one describing the well's tie to this unit.
    // LTD slot rows carry no UWI, so they fall back to well name, which is unique.
    const seenWells = new Set<string>();
    const uniqueWells = tableData.filter((item) => {
      const key = item.slotId || item.wellName;
      if (!key) return true;
      if (seenWells.has(key)) return false;
      seenWells.add(key);
      return true;
    });

    // Partition data into assigned and not assigned
    const [assignedItems, notAssignedItems] = partition(
      uniqueWells,
      (item) => item.assignedToUnit === 'Yes'
    );

    // For development areas, LTDs from other units are already filtered from tableData
    // For non-development areas, include all LTDs in the count
    const notAssignedItemsForSummary = notAssignedItems;

    // Helper function to count statuses
    const countStatuses = (
      items: typeof tableData
    ): Omit<SummaryRow, 'summary'> => {
      const counts: Omit<SummaryRow, 'summary'> = {
        producing: 0,
        duc: 0,
        permit: 0,
        ltd: 0,
        shutIn: 0,
        inactive: 0,
        expiredPermit: 0,
        ...wellStatusCounts(),
        total: 0,
      };

      items.forEach((item) => {
        const statusKey = statusMap[item.srpWellStatus?.toLowerCase()];
        if (statusKey) {
          counts[statusKey]++;
        }
        counts.total++;
      });

      return counts;
    };

    const assignedCounts = countStatuses(assignedItems);
    const notAssignedCounts = countStatuses(notAssignedItemsForSummary);

    return [
      { summary: 'Assigned', ...assignedCounts },
      { summary: 'Not Assigned', ...notAssignedCounts },
    ];
  }, [tableData]);

  const table = useReactTable({
    data: tableData,
    columns,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnSizing,
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: matchesDisplayValue,
    // TanStack's default gates global filtering on the type of the *first*
    // row's value, so a null in row 0 would silently drop a column out of
    // search. Let every accessor column through and leave the decision to
    // matchesDisplayValue.
    getColumnCanGlobalFilter: () => true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {
      size: 150,
      minSize: 50,
      enableResizing: true,
      enableColumnFilter: true,
    },
    initialState: {
      columnSizing: {},
    },
  });

  const { rows } = table.getRowModel();
  const parentRef = useRef<HTMLDivElement>(null);

  // Removed virtualizer and all related code

  const isColumnVisible = table.getVisibleLeafColumns().length > 0;

  // Show loading state
  if (uiaSlotsLoading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableContext}>
          <TableActions
            table={table}
            isColumnFilterActive={isColumnFilterActive}
            setIsColumnFilterActive={setIsColumnFilterActive}
          />
        </div>
        <div className={styles.stackTableContainer}>
          <div ref={parentRef} className={styles.stackTableArea}>
            <table
              className={styles.stackTable}
              style={{ width: table.getTotalSize() }}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Fragment key={headerGroup.id}>
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={styles.stackTableHeader}
                          style={{
                            width: header.getSize(),
                            position: 'relative',
                          }}
                        >
                          {header.isPlaceholder ? null : (
                            <div>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                    {isColumnFilterActive ? (
                      <tr>
                        <ColumnFilter
                          key={`filter-row-${headerGroup.id}`}
                          pinStyleGenerator={emptyPinStyleGenerator}
                          headers={headerGroup.headers}
                        />
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </thead>
              <tbody className={styles.stackTableBody}>
                <SkeletonTableRows rows={10} columns={columns.length} />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (!tableData.length) {
    return null;
  }

  return (
    <>
      {/* Summary Statistics Table */}
      <div className={styles.summaryTableContainer}>
        <Table size="sm">
          <TableHead>
            <TableRow>
              <TableHeader>Summary</TableHeader>
              <TableHeader>Producing</TableHeader>
              <TableHeader>DUC</TableHeader>
              <TableHeader>Permit</TableHeader>
              <TableHeader>LTD</TableHeader>
              <TableHeader>Shut In</TableHeader>
              <TableHeader>Inactive</TableHeader>
              <TableHeader>Expired Permit</TableHeader>
              {WELL_STATUS_COUNT_COLUMNS.map(({ key, label }) => (
                <TableHeader key={key}>{label}</TableHeader>
              ))}
              <TableHeader>Total</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {slotSummaryData.map((row) => (
              <TableRow key={row.summary}>
                <TableCell>{row.summary}</TableCell>
                <TableCell>
                  <FormattedCount value={row.producing} />
                </TableCell>
                <TableCell>
                  <FormattedCount value={row.duc} />
                </TableCell>
                <TableCell>
                  <FormattedCount value={row.permit} />
                </TableCell>
                <TableCell>
                  <FormattedCount value={row.ltd} />
                </TableCell>
                <TableCell>
                  <FormattedCount value={row.shutIn} />
                </TableCell>
                <TableCell>
                  <FormattedCount value={row.inactive} />
                </TableCell>
                <TableCell>
                  <FormattedCount value={row.expiredPermit} />
                </TableCell>
                {WELL_STATUS_COUNT_COLUMNS.map(({ key }) => (
                  <TableCell key={key}>
                    <FormattedCount value={row[key]} />
                  </TableCell>
                ))}
                <TableCell>
                  <FormattedCount value={row.total} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.tableContext}>
          <Search
            labelText=""
            placeholder="Search wells..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            size="lg"
            className={styles.tableSearch}
          />
          <div className={styles.thresholdMenuWrapper}>
            <OverflowMenu
              renderIcon={ShapeExcept}
              size="lg"
              flipped
              className={styles.thresholdOverflowMenu}
            >
              <div
                id="threshold-coverage-menu"
                className={styles.thresholdMenu}
                data-threshold-menu="true"
              >
                <h4 className={styles.menuTitle}>Unit-Well Coverage</h4>
                <p className={styles.menuDescription}>
                  The Unit-Well Coverage Threshold determines which Units are
                  recognized based on their overlap to the Well. Units with an
                  overlap to the Well higher than the selected threshold are
                  classified as intersecting, while those below this threshold
                  are ignored. The default threshold is 3%.
                </p>
                <p className={styles.menuDescription}>
                  Select a threshold to determine which Units are recognized as
                  overlapping. Lower values include more Units with minor
                  overlaps, while higher values filter them out.
                </p>
                <RadioButtonGroup
                  name="coverage-threshold"
                  valueSelected={coverageThreshold * 100}
                  onChange={(value: string | number | undefined) =>
                    setCoverageThreshold((value as number) / 100)
                  }
                  orientation="horizontal"
                  className={styles.radioGroup}
                >
                  <RadioButton id="threshold-none" value={0} labelText="None" />
                  <RadioButton id="threshold-1" value={1} labelText="1%" />
                  <RadioButton id="threshold-2" value={2} labelText="2%" />
                  <RadioButton id="threshold-3" value={3} labelText="3%" />
                  <RadioButton id="threshold-4" value={4} labelText="4%" />
                  <RadioButton id="threshold-5" value={5} labelText="5%" />
                </RadioButtonGroup>
              </div>
            </OverflowMenu>
          </div>

          <Button
            kind="ghost"
            size="lg"
            hasIconOnly
            renderIcon={Compare}
            iconDescription="Compare allocation managers"
            tooltipPosition="bottom"
            disabled={!onCompare}
            onClick={onCompare}
          />
          <TableActions
            table={table}
            isColumnFilterActive={isColumnFilterActive}
            setIsColumnFilterActive={setIsColumnFilterActive}
          />
        </div>

        {isColumnVisible ? (
          <div className={styles.stackTableContainer}>
            <div ref={parentRef} className={styles.stackTableArea}>
              <table
                className={styles.stackTable}
                style={{ width: table.getTotalSize() }}
              >
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Fragment key={headerGroup.id}>
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={styles.stackTableHeader}
                            style={{
                              width: header.getSize(),
                              position: 'relative',
                            }}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={
                                  header.column.getCanSort()
                                    ? 'cursor-pointer select-none'
                                    : ''
                                }
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                                {header.column.getIsSorted() && (
                                  <span className={styles.sortIcon}>
                                    {header.column.getIsSorted() === 'asc' ? (
                                      <CaretUp />
                                    ) : (
                                      <CaretDown />
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                            {header.column.getCanResize() && (
                              <div
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className={`${styles.resizer} ${
                                  header.column.getIsResizing()
                                    ? styles.isResizing
                                    : ''
                                }`}
                              />
                            )}
                          </th>
                        ))}
                      </tr>
                      {isColumnFilterActive ? (
                        <tr>
                          <ColumnFilter
                            key={`filter-row-${headerGroup.id}`}
                            pinStyleGenerator={emptyPinStyleGenerator}
                            headers={headerGroup.headers}
                          />
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </thead>
                <tbody className={styles.stackTableBody}>
                  {rows.map((row) => {
                    const slotId = row.original.slotId;
                    const wellName = row.original.wellName;
                    const slotUnit = row.original.unit;
                    // The unit has to match as well as the well: a well
                    // allocated under two agreements renders two rows sharing
                    // a slotId, and matching on slotId alone highlights both.
                    const isSelected =
                      selectedSlotUnit === slotUnit &&
                      ((!isNil(selectedWellUWI) &&
                        selectedWellUWI === slotId) ||
                        (!isNil(selectedWellName) &&
                          selectedWellName === wellName));
                    return (
                      <tr
                        key={row.id}
                        onClick={() => {
                          onWellSelect(
                            slotId ?? null,
                            wellName ?? null,
                            slotUnit
                          );
                        }}
                        className={isSelected ? styles.selectedRow : ''}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            style={{
                              width: cell.column.getSize(),
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className={styles.hiddenColumn}>
            No columns are visible. Use the column visibility controls to show
            columns.
          </div>
        )}
      </div>
    </>
  );
};

export const SlotsDetail = ({
  unitName,
  selectedWellUWI,
  selectedWellName,
  selectedSlotUnit,
  onWellSelect,
  onCompare,
}: SlotsDetailProps) => {
  return (
    <SlotsTable
      unitName={unitName}
      selectedWellUWI={selectedWellUWI}
      selectedWellName={selectedWellName}
      selectedSlotUnit={selectedSlotUnit}
      onWellSelect={onWellSelect}
      onCompare={onCompare}
    />
  );
};
